import { LapTable, RaceConfig } from "../types/raceData";
import { FlagPeriod } from "../types/interventions";

export function getEnergyValue(lapTable: LapTable, lapNumber: number) {
  const lap = lapTable[lapNumber];
  if (!lap) return { value: null, isReal: false };
  const isReal = lap.energy !== null;
  const value = isReal ? lap.energy : lap.predictedEnergy;
  return { value, isReal };
}

export function getLapTimeValue(lapTable: LapTable, lapNumber: number) {
  const lap = lapTable[lapNumber];
  const isReal = lap.lapTime !== null;
  const value = isReal ? lap.lapTime : lap.predictedLapTime;
  return { value, isReal };
}

export function getPitInSoCValue(lapTable: LapTable, lapNumber: number) {
  const lap = lapTable[lapNumber];
  if (!lap) return { value: null, isReal: false };
  const isReal = lap.pitInSoC !== null;
  const value = isReal ? lap.pitInSoC : lap.predictedPitInSoC;
  return { value, isReal };
}

export function getRealTCellMax(lapTable: LapTable, lapNumber: number) {
  return lapTable[lapNumber]?.tCellMax ?? null;
}

export function getTCellMaxValue(
  lapTable: LapTable,
  lapNumbers: number[],
  lapNumber: number,
) {
  const realValue = getRealTCellMax(lapTable, lapNumber);
  if (realValue !== null) return { value: realValue, isReal: true };

  const currentIndex = lapNumbers.indexOf(lapNumber);
  let previousReading: { lapNumber: number; value: number } | null = null;
  let nextReading: { lapNumber: number; value: number } | null = null;

  for (let i = currentIndex - 1; i >= 0; i--) {
    const value = getRealTCellMax(lapTable, lapNumbers[i]);
    if (value !== null) {
      previousReading = { lapNumber: lapNumbers[i], value };
      break;
    }
  }

  for (let i = currentIndex + 1; i < lapNumbers.length; i++) {
    const value = getRealTCellMax(lapTable, lapNumbers[i]);
    if (value !== null) {
      nextReading = { lapNumber: lapNumbers[i], value };
      break;
    }
  }

  if (previousReading && nextReading) {
    const progress =
      (lapNumber - previousReading.lapNumber) /
      (nextReading.lapNumber - previousReading.lapNumber);
    return {
      value:
        previousReading.value +
        (nextReading.value - previousReading.value) * progress,
      isReal: false,
    };
  }

  return {
    value: lapTable[lapNumber]?.predictedTCellMax ?? null,
    isReal: false,
  };
}

export function getRealEnergy(lapTable: LapTable, lapNumber: number) {
  const energy = lapTable[lapNumber]?.energy;
  return energy !== null && energy !== undefined ? energy : null;
}

// Latest lap with real energy, regardless of currentLap.
export function getLastRealEnergy(lapTable: LapTable) {
  const lap = Object.values(lapTable)
    .filter((lap) => lap.energy !== null)
    .at(-1);
  return lap ? { lapNumber: lap.lapNumber, energy: lap.energy } : null;
}

// Latest lap at or before maxLapNumber where both cars have real energy.
export function getLastCommonRealEnergy(
  lapTableA: LapTable,
  lapTableB: LapTable,
  maxLapNumber: number,
) {
  for (let lapNumber = maxLapNumber; lapNumber >= 0; lapNumber--) {
    const energyA = getRealEnergy(lapTableA, lapNumber);
    const energyB = getRealEnergy(lapTableB, lapNumber);
    if (energyA !== null && energyB !== null) {
      return { lapNumber, energyA, energyB };
    }
  }
  return null;
}

export function getPitWindowOpenCloseTargets(
  lapNumber: number,
  lapTable: LapTable,
  raceConfig: RaceConfig,
  EConsumptionLastCorner2PitIn: number,
) {
  const lastReal = getLastRealEnergy(lapTable);
  const realEnergyLap = lastReal?.lapNumber ?? 0;
  const realEnergyRem =
    lastReal === null ? lapTable[0]?.predictedEnergy ?? null : lastReal.energy;

  if (realEnergyRem === null || lapNumber <= realEnergyLap) {
    return { open: null, close: null };
  }

  const pitWindowCloseEnergy = raceConfig.pitWindowCloseEnergy ?? null;
  const pitWindowOpenEnergy = raceConfig.pitWindowOpenEnergy ?? null;

  if (pitWindowCloseEnergy === null || pitWindowOpenEnergy === null) {
    return { open: null, close: null };
  }

  const lapsFromRealEnergy = lapNumber - realEnergyLap;

  return {
    open:
      (realEnergyRem - pitWindowOpenEnergy - EConsumptionLastCorner2PitIn) /
      lapsFromRealEnergy,
    close:
      (realEnergyRem - pitWindowCloseEnergy - EConsumptionLastCorner2PitIn) /
      lapsFromRealEnergy,
  };
}

export function getPitWindowOpenCloseBars(
  lapNumber: number,
  lapTable: LapTable,
  raceConfig: RaceConfig,
  attackMode: boolean,
  nLapsPitWindowOpenOffset: number,
  nLapsPitWindowCloseOffset: number,
) {
  const target = attackMode
    ? raceConfig.targetAttack ?? 0
    : raceConfig.targetRace ?? 0;

  const energyRem = getEnergyValue(lapTable, lapNumber - 1).value;

  const pitWindowOpenEnergy = raceConfig.pitWindowOpenEnergy ?? null;
  const pitWindowCloseEnergy = raceConfig.pitWindowCloseEnergy ?? null;

  if (
    pitWindowOpenEnergy === null ||
    pitWindowCloseEnergy === null ||
    energyRem === null ||
    target === null
  )
    return { open: null, close: null };

  return {
    open: (energyRem - pitWindowOpenEnergy) / target + nLapsPitWindowOpenOffset,
    close:
      (energyRem - pitWindowCloseEnergy) / target + nLapsPitWindowCloseOffset,
  };
}

export function getLapFlagGradient(
  lapTable: LapTable,
  lapNumber: number,
  flagPeriods: FlagPeriod[] | undefined,
) {
  const lap = lapTable[lapNumber];
  if (lap?.lapStartTime == null || lap?.lapTime == null) return undefined;

  const lapStartTime = lap.lapStartTime;
  const lapEndTime = lapStartTime + lap.lapTime * 1000;
  if (lapEndTime <= lapStartTime) return undefined;

  const lapDuration = lapEndTime - lapStartTime;
  const toPct = (time: number) => ((time - lapStartTime) / lapDuration) * 100;

  const flag_period_colours: Record<string, string> = {
    FULL_YELLOW: "#ffd43b",
    SAFETY_CAR: "#ffa94d",
    RED: "#ff6b6b",
  };

  const relevantPeriods = (flagPeriods ?? [])
    .filter((period) => flag_period_colours[period.type] != null)
    .map((period) => ({
      colour: flag_period_colours[period.type],
      start: Math.max(period.startTime, lapStartTime),
      end: Math.min(period.endTime ?? lapEndTime, lapEndTime),
    }));

  if (relevantPeriods.length === 0) return undefined;

  const stops: string[] = [];
  let currentTime = lapStartTime;

  for (const period of relevantPeriods) {
    if (period.start > currentTime) {
      stops.push(
        `transparent ${toPct(currentTime)}%`,
        `transparent ${toPct(period.start)}%`,
      );
    }
    stops.push(
      `${period.colour} ${toPct(period.start)}%`,
      `${period.colour} ${toPct(period.end)}%`,
    );
    currentTime = period.end;
  }

  if (currentTime < lapEndTime) {
    stops.push(`transparent ${toPct(currentTime)}%`, "transparent 100%");
  }

  return `linear-gradient(to right, ${stops.join(", ")})`;
}

export function getTimeRemainingValues(
  lapTable: LapTable,
  lapNumber: number,
  tRaceMaximum: number | undefined,
  tRaceMaximumWithSuspension: number | undefined,
  flagPeriods: FlagPeriod[] | undefined,
) {
  const elapsedTime = Object.keys(lapTable)
    .map(Number)
    .filter((lapNum) => lapNum <= lapNumber)
    .reduce((total, lapNum) => {
      const lap = lapTable[lapNum];
      return total + (lap.lapTime ?? lap.predictedLapTime ?? 0);
    }, 0);

  const lap = lapTable[lapNumber];
  let lapStartTime = lap?.lapStartTime;

  // If lap start time isn't available, fall back to the last known lap start time plus accumulated lap times
  if (lapStartTime == null) {
    let elapsedMilliseconds = 0;

    for (let lapNum = lapNumber - 1; lapNum >= 1; lapNum--) {
      const priorLap = lapTable[lapNum];
      if (!priorLap) break;

      const lapDuration = priorLap.lapTime ?? priorLap.predictedLapTime;
      if (lapDuration == null) break;
      elapsedMilliseconds += lapDuration * 1000;

      if (priorLap.lapStartTime != null) {
        lapStartTime = priorLap.lapStartTime + elapsedMilliseconds;
        break;
      }
    }
  }

  const lapEndTime =
    lapStartTime != null
      ? lapStartTime + (lap?.lapTime ?? lap?.predictedLapTime ?? 0) * 1000
      : null;

  const stoppedSeconds =
    lapEndTime != null
      ? (flagPeriods ?? [])
          .filter(
            (period) => period.type === "RED" && period.startTime < lapEndTime,
          )
          .reduce((total, period) => {
            const endTime = Math.min(period.endTime ?? lapEndTime, lapEndTime);
            return total + (endTime - period.startTime) / 1000;
          }, 0)
      : 0;

  return {
    race:
      tRaceMaximum != null ? tRaceMaximum + stoppedSeconds - elapsedTime : null,
    max:
      tRaceMaximumWithSuspension != null
        ? tRaceMaximumWithSuspension - elapsedTime
        : null,
  };
}

// -1, window closed, 0 borderline, 1 window open
const PIT_WINDOW_BORDERLINE_SOC_MARGIN = 0.5;
export function getPitWindowStatus(
  pitInSoC: number | null,
  pitEntrySoCToOpen: number,
  pitEntrySoCToClose: number,
) {
  if (
    pitInSoC === null ||
    pitEntrySoCToOpen === null ||
    pitEntrySoCToClose === null
  )
    return null;

  const isNearOpen =
    Math.abs(pitInSoC - pitEntrySoCToOpen) <= PIT_WINDOW_BORDERLINE_SOC_MARGIN;
  const isNearClose =
    Math.abs(pitInSoC - pitEntrySoCToClose) <= PIT_WINDOW_BORDERLINE_SOC_MARGIN;
  if (isNearOpen || isNearClose) return 0;

  return pitInSoC <= pitEntrySoCToOpen && pitInSoC >= pitEntrySoCToClose
    ? 1
    : -1;
}
